import { Skeleton } from "@/components/ui/skeleton";
import type { ProjectPulse } from "@/hooks/use-project-pulse";
import { cn } from "@/lib/utils";

interface PulseStripProps {
  pulse: ProjectPulse | undefined;
  scheduleProgress?: number | null;
  isLoading: boolean;
}

function moneyShort(n: number): string {
  if (Math.abs(n) >= 1_000_000) {
    const m = n / 1_000_000;
    const trimmed = m % 1 === 0 ? m.toFixed(0) : m.toFixed(2).replace(/\.?0+$/, "");
    return `$${trimmed}M`;
  }
  if (Math.abs(n) >= 1_000) {
    return `$${Math.round(n / 1_000)}K`;
  }
  return `$${Math.round(n).toLocaleString()}`;
}

export function PulseStrip({ pulse, scheduleProgress, isLoading }: PulseStripProps) {
  if (isLoading || !pulse) {
    return (
      <div className="rounded-xl border border-border bg-card shadow-sm overflow-hidden">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 divide-x divide-border">
          {["a", "b", "c", "d", "e", "f"].map((k) => (
            <div key={k} className="px-5 py-4 space-y-2">
              <Skeleton className="h-3 w-20" />
              <Skeleton className="h-6 w-24" />
              <Skeleton className="h-3 w-16" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  const billedPct =
    pulse.revisedContract > 0 ? (pulse.billedToDate / pulse.revisedContract) * 100 : 0;
  const isSub = pulse.variant === "sub";
  const blockingTone = isSub ? pulse.blockingSubs > 0 : pulse.blockingSubs > 0;

  const subComplianceLabel = isSub
    ? pulse.blockingSubs === 0
      ? "Compliant"
      : "Action required"
    : null;

  const cells: Array<{
    label: string;
    value: string;
    sub?: string | null;
    tone?: "warn";
  }> = [
    {
      label: isSub ? "My commitment (revised)" : "Revised contract",
      value: moneyShort(pulse.revisedContract),
      sub:
        pulse.approvedChangeOrders > 0
          ? `+${moneyShort(pulse.approvedChangeOrders)} executed COs`
          : null,
    },
    {
      label: isSub ? "My billed to date" : "Billed to date",
      value: moneyShort(pulse.billedToDate),
      sub: `${billedPct.toFixed(1)}% of ${isSub ? "commitment" : "contract"}`,
    },
    {
      label: isSub ? "My scope % complete" : "% Complete",
      value: `${pulse.pctComplete}%`,
      sub:
        scheduleProgress != null && scheduleProgress > 0 ? `Schedule: ${scheduleProgress}%` : null,
    },
    ...(isSub
      ? []
      : [
          {
            label: "Buyout",
            value: `${pulse.buyoutPct}%`,
            sub:
              pulse.buyoutBudget > 0
                ? `${moneyShort(pulse.buyoutCommitted)} of ${moneyShort(pulse.buyoutBudget)}`
                : null,
          },
        ]),
    {
      label: isSub ? "My retainage held" : "Retainage held",
      value: moneyShort(pulse.retainage),
    },
    isSub
      ? {
          label: "My compliance status",
          value: subComplianceLabel ?? "—",
          sub:
            pulse.blockingSubs > 0
              ? `${pulse.blockingSubs} outstanding`
              : pulse.totalSubs === 0
                ? "No requirements"
                : "All current",
          tone: blockingTone ? ("warn" as const) : undefined,
        }
      : {
          label: "Compliant subs",
          value: `${pulse.compliantSubs}/${pulse.totalSubs}`,
          sub: blockingTone ? `${pulse.blockingSubs} blocking` : "all clear",
          tone: blockingTone ? ("warn" as const) : undefined,
        },
  ];

  return (
    <div className="rounded-xl border border-border bg-card shadow-sm overflow-hidden">
      <div
        className={cn(
          "grid grid-cols-2 md:grid-cols-3 divide-x divide-border",
          isSub ? "lg:grid-cols-5" : "lg:grid-cols-6",
        )}
      >
        {cells.map((c) => (
          <div key={c.label} className="px-5 py-4">
            <div className="text-[10.5px] uppercase tracking-[0.08em] text-muted-foreground font-semibold">
              {c.label}
            </div>
            <div
              className={cn(
                "mt-1 text-xl font-semibold tabular-nums leading-tight",
                c.tone === "warn" && "text-amber-700",
              )}
            >
              {c.value}
            </div>
            {c.sub ? (
              <div className="mt-0.5 text-[11.5px] text-muted-foreground truncate">{c.sub}</div>
            ) : null}
          </div>
        ))}
      </div>
    </div>
  );
}
